import { useEffect, useRef } from 'react'

import type {
  AttributeBoundary,
  Nullable,
  Point3D,
  Property,
  Range2D,
  ScaleType,
  UpdateValue,
} from '../../../../types'
import { convertGlobalGeologicalCoordinatesToLocal, requestRedraw } from '../../../../utils'
import { LineRender } from '../LineRender'

const getPoints = (points: Point3D[][], origin: Range2D): Point3D[][] =>
  points.map(line =>
    line.map(
      point =>
        ({
          ...convertGlobalGeologicalCoordinatesToLocal(origin, point),
          z: point.z,
        }) satisfies Point3D,
    ),
  )

export const useShader = (
  containerElement: Nullable<HTMLCanvasElement>,
  programs: Property<Record<string, WebGLProgram>>,
  scale: ScaleType,
  origin: Range2D,
  lineWidth: number,
  points: Point3D[][],
  palette: number[],
  attributeBoundary: AttributeBoundary | undefined,
  setUpdate: UpdateValue<number>,
) => {
  const lineRenderRef = useRef<Nullable<LineRender>>(null)

  const dispose = () => {
    lineRenderRef.current?.[Symbol.dispose]()
    lineRenderRef.current = null
    requestRedraw(setUpdate)
  }

  useEffect(() => {
    if (lineRenderRef.current || !containerElement) return dispose

    lineRenderRef.current = new LineRender(
      containerElement,
      programs,
      scale,
      lineWidth,
      getPoints(points, origin),
      palette,
    )

    if (attributeBoundary) {
      lineRenderRef.current.setRange([attributeBoundary.stop, attributeBoundary.start])
    }

    requestRedraw(setUpdate)

    return dispose
  }, [containerElement, setUpdate, palette])

  useEffect(() => {
    if (!lineRenderRef.current) return

    lineRenderRef.current.setPoints(getPoints(points, origin))
  }, [points, origin.x, origin.y, origin.width, origin.height, setUpdate])

  useEffect(() => {
    if (!lineRenderRef.current) return

    lineRenderRef.current.setZoomState(scale)

    requestRedraw(setUpdate)
  }, [scale, setUpdate])

  useEffect(() => {
    if (!lineRenderRef.current) return

    lineRenderRef.current.setLineWidth(lineWidth)
  }, [lineWidth])

  useEffect(() => {
    if (!lineRenderRef.current || !attributeBoundary) return

    lineRenderRef.current.setRange([attributeBoundary.stop, attributeBoundary.start])
    requestRedraw(setUpdate)
  }, [attributeBoundary, setUpdate])

  return lineRenderRef.current
}
